import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiMail, 
  FiPhone, 
  FiMapPin,
  FiFacebook,
  FiTwitter,
  FiLinkedin,
  FiInstagram 
} from 'react-icons/fi';
import { Logo } from '../common/Logo';

export const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const services = [
    { name: 'SEO Services', href: '/services/seo' },
    { name: 'PPC Advertising', href: '/services/ppc' },
    { name: 'Web Design', href: '/services/web-design' },
    { name: 'Social Media', href: '/services/social-media' },
    { name: 'Content Marketing', href: '/services/content-marketing' },
    { name: 'Consulting', href: '/services/consulting' },
  ];

  const company = [
    { name: 'About Us', href: '/about' },
    { name: 'Our Team', href: '/about#team' },
    { name: 'Case Studies', href: '/case-studies' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' },
    { name: 'Careers', href: '/careers' },
  ];

  const resources = [
    { name: 'Free SEO Audit', href: '/free-audit' },
    { name: 'Digital Marketing Guide', href: '/guide' },
    { name: 'ROI Calculator', href: '/roi-calculator' },
    { name: 'Industry Reports', href: '/reports' },
    { name: 'Webinars', href: '/webinars' },
    { name: 'Support Center', href: '/support' },
  ];

  const socialLinks = [
    { icon: FiFacebook, href: 'https://facebook.com/miranimarketing', label: 'Facebook' },
    { icon: FiTwitter, href: 'https://twitter.com/miranimarketing', label: 'Twitter' },
    { icon: FiLinkedin, href: 'https://linkedin.com/company/miranimarketing', label: 'LinkedIn' },
    { icon: FiInstagram, href: 'https://instagram.com/miranimarketing', label: 'Instagram' },
  ];

  return (
    <footer className="bg-secondary-800 text-white">
      {/* Main Footer */}
      <div className="container-width py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center mb-6">
              <Logo className="h-10" variant="white" />
            </Link>
            
            <p className="text-gray-300 mb-6 leading-relaxed">
              We're a full-service digital marketing agency dedicated to helping businesses grow through innovative online strategies and data-driven results.
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <a 
                href="mailto:hello@miranimarketing.com"
                className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors"
              >
                <FiMail className="w-5 h-5 text-primary-400" />
                <span>hello@miranimarketing.com</span>
              </a>
              
              <a 
                href="tel:+919313258355"
                className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors"
              >
                <FiPhone className="w-5 h-5 text-primary-500" />
                <span>+91-9313258355, +91-9624361042</span>
              </a>
              
              <a 
                href="https://www.google.com/maps/search/?api=1&query=301+Ila+Bhavan+Kantha+Gali+Popat+Mahallo+Nanpura+Surat+Gujarat+395001"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start space-x-3 text-gray-300 hover:text-white transition-colors"
              >
                <FiMapPin className="w-5 h-5 text-primary-400 flex-shrink-0 mt-1" />
                <span>301, Ila Bhavan, Kantha Gali,<br />Popat Mahallo, Nanpura,<br />Surat, Gujarat, India 395001</span>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.href}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Company</h3>
            <ul className="space-y-3">
              {company.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Resources</h3>
            <ul className="space-y-3">
              {resources.map((resource) => (
                <li key={resource.name}>
                  <Link
                    to={resource.href}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {resource.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="max-w-md">
            <h3 className="text-lg font-semibold mb-4">Stay Updated</h3>
            <p className="text-gray-300 mb-4">
              Get the latest digital marketing tips and industry insights delivered to your inbox.
            </p>
            <form className="flex space-x-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-white placeholder-gray-400"
                required
              />
              <button
                type="submit"
                className="px-6 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg font-medium transition-colors"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container-width py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Copyright */}
            <div className="text-gray-400 text-sm">
              © {currentYear} Mirani Marketing. All rights reserved.
            </div>

            {/* Social Links */}
            <div className="flex items-center space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-primary-600 transition-colors"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>

            {/* Legal Links */}
            <div className="flex items-center space-x-6 text-sm">
              <Link
                to="/privacy"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Terms of Service
              </Link>
              <Link
                to="/sitemap"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Sitemap
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};